import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { toast } from "sonner";
import { env } from "@/env";
import { useActiveOrganization } from "@/hooks/use-organizations";
import { authClient } from "@/lib/auth-client";
import type { MembershipTier, MembershipTierConfig } from "@/types/membership";
import type {
  CatalogPlan,
  CustomerMeter,
  Order,
  PendingPlanChange,
  Product,
  ProductsResponse,
  Subscription,
  SubscriptionsResponse,
} from "./membership/membership-types";
import {
  buildTierConfigFromPlansAndProducts as buildTierConfigs,
  parseTierIdFromName as parseTierIdFromNameInternal,
  parseTierIdFromProduct as parseTierIdFromProductInternal,
} from "./membership/tier-config";
import { queryKeys } from "@/lib/query-keys";

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const FIVE_MINUTES = 5;
const TWO_MINUTES = 2;

const CUSTOMER_STATE_STALE_TIME = MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE * FIVE_MINUTES;
const SUBSCRIPTIONS_STALE_TIME = MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE * TWO_MINUTES;

const EMPTY_PRODUCTS: Product[] = [];
const EMPTY_SUBSCRIPTIONS: Subscription[] = [];
const EMPTY_PENDING_PLAN_CHANGES: PendingPlanChange[] = [];
const EMPTY_ORDERS: Order[] = [];
const EMPTY_METERS: CustomerMeter[] = [];
const EMPTY_BENEFITS: string[] = [];

export type { CustomerMeter, Order, Subscription } from "./membership/membership-types";
export type { PendingPlanChange } from "./membership/membership-types";

export type PlanChangeType = "upgrade" | "downgrade" | "same" | "period_change";

export type PlanChangeResult = {
  success: boolean;
  planChange?: AcceptedPlanChangeResponse;
  subscription?: {
    id: string;
    status: string;
    productId: string;
    amount: number;
    currency: string;
    currentPeriodEnd: string;
    recurringInterval: string;
  };
  error?: string;
};

type AdminTierOverrideTarget = {
  type: "user" | "organization";
  id: string;
};

type AdminTierOverrideResponse = {
  target: AdminTierOverrideTarget;
  tier: string;
  billingCadence: "monthly" | "yearly" | null;
  realSubscriptionsUnchanged: boolean;
};

export type AdminTierOverrideStatus = {
  target: AdminTierOverrideTarget;
  tier: MembershipTier;
  billingCadence: "monthly" | "yearly" | null;
  realSubscriptionsUnchanged: boolean;
};

type MembershipStatusResponse = {
  success: boolean;
  data: {
    userId: string;
    tier: string;
    hasActiveSubscription: boolean;
    benefits: unknown[];
    expiresAt?: string;
    billingCadence?: "monthly" | "yearly" | null;
    organizationId?: string;
    isAppAdmin?: boolean;
    adminTierOverride?: AdminTierOverrideResponse;
  };
};

type ClearAdminTierOverrideResponse = {
  success: boolean;
  resultKind: "admin-tier-override-cleared";
  changed: boolean;
};

type AcceptedUpgradePlanChangeResponse = {
  checkoutId: string;
  checkoutUrl: string;
  currentSubscriptionId: string;
  direction: "upgrade";
  membershipDeferredUntil: "payment_confirmation";
  targetBillingCadence: "monthly" | "yearly";
  targetProductId: string;
  targetTier: string;
};

type AcceptedRenewalPlanChangeResponse = {
  currentSubscriptionId: string;
  direction: "downgrade" | "cadence_change";
  effectiveTiming: "renewal";
  membershipDeferredUntil: "renewal";
  pendingPlanChange: PendingPlanChange;
  targetBillingCadence: "monthly" | "yearly";
  targetProductId: string;
  targetTier: string;
};

type AcceptedPlanChangeResponse =
  | AcceptedUpgradePlanChangeResponse
  | AcceptedRenewalPlanChangeResponse;

type PlanChangeApiResponse = {
  subscription?: Subscription;
  planChange?: AcceptedPlanChangeResponse;
};

type CancelPendingPlanChangeResponse = {
  pendingPlanChange: PendingPlanChange;
};

type CheckoutResponse = {
  checkoutUrl?: string;
  resultKind?: string;
  changed?: boolean;
};

export type MembershipContextValue = {
  currentTier: MembershipTier;
  tierConfig: MembershipTierConfig | null;
  isLoading: boolean;
  error: Error | null;
  subscriptions: Subscription[];
  activeSubscription: Subscription | null;
  pendingPlanChanges: PendingPlanChange[];
  pendingPlanChange: PendingPlanChange | null;
  orders: Order[];
  meters: CustomerMeter[];
  benefits: string[];
  billingCadence: "monthly" | "yearly" | null;
  hasActiveSubscription: boolean;
  upgradeToTier: (
    tierId: string,
    billingPeriod?: "monthly" | "yearly",
    organizationId?: string,
  ) => Promise<void>;
  changePlan: (
    productId: string,
    options?: {
      billingPeriod?: "monthly" | "yearly";
      organizationId?: string;
      prorationBehavior?: "invoice" | "prorate";
    },
  ) => Promise<PlanChangeResult>;
  openBillingPortal: (returnUrl?: string) => Promise<void>;
  refreshMembership: () => Promise<void>;
  clearAdminTierOverride: () => Promise<void>;
  cancelPendingPlanChange: (subscriptionId?: string) => Promise<void>;
  hasFeature: (feature: string) => boolean;
  hasPermission: (permission: string) => boolean;
  isWithinLimit: (limitKey: string, currentUsage: number) => boolean;
  canUpgrade: boolean;
  getPlanChangeType: (
    targetTierId: string,
    targetBillingPeriod?: "monthly" | "yearly",
  ) => PlanChangeType;
  adminTierOverride: AdminTierOverrideStatus | null;
  isAppAdmin: boolean;
  isClearingAdminTierOverride: boolean;
  isCancelingPendingPlanChange: boolean;
};

function createMembershipContext() {
  return createContext<MembershipContextValue | null>(null);
}

const MembershipContext: ReturnType<typeof createMembershipContext> =
  import.meta.hot?.data.membershipContext ?? createMembershipContext();

if (import.meta.hot) {
  import.meta.hot.data.membershipContext = MembershipContext;
}

function parseTierIdFromName(raw: string | undefined): MembershipTier {
  return parseTierIdFromNameInternal(raw);
}

function parseTierIdFromProduct(product: Product): MembershipTier {
  return parseTierIdFromProductInternal(product);
}

function buildTierConfigFromPlansAndProducts(
  products: Product[],
  plans: CatalogPlan[] | undefined,
  provider: "polar" | "mercadopago",
): MembershipTierConfig[] {
  return buildTierConfigs(products, plans, provider);
}

function isAdminTierOverrideCheckoutResponse(data: CheckoutResponse): boolean {
  return data.resultKind === "admin-tier-override";
}

function getAdminTierOverrideToastMessage(data: CheckoutResponse): string {
  return data.changed ? "Admin tier override applied." : "Admin tier override already active.";
}

function parseAdminTierOverride(
  data: AdminTierOverrideResponse | undefined,
): AdminTierOverrideStatus | null {
  if (!data) {
    return null;
  }

  return {
    ...data,
    tier: parseTierIdFromName(data.tier),
  };
}

function isUsableSubscription(subscription: Subscription): boolean {
  if (subscription.status === "active" || subscription.status === "trialing") {
    return true;
  }

  return (
    subscription.status === "canceled" &&
    subscription.currentPeriodEnd !== undefined &&
    new Date(subscription.currentPeriodEnd) > new Date()
  );
}

export function useMembership() {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error("useMembership must be used within a MembershipProvider");
  }
  return context;
}

export type MembershipProviderProps = {
  children: React.ReactNode;
};

export function MembershipProvider({ children }: MembershipProviderProps) {
  const queryClient = useQueryClient();
  const { data: activeOrganization } = useActiveOrganization();
  const isOrganizationSubscriptionMode = env.VITE_SUBSCRIPTION_MODE === "organization";
  const activeOrganizationId = isOrganizationSubscriptionMode ? activeOrganization?.id : undefined;

  const productsQuery = useQuery({
    queryKey: queryKeys.subscriptions.products(),
    queryFn: async (): Promise<ProductsResponse> => {
      const response = await fetch(`${env.VITE_API_URL}/api/subscriptions/products`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch subscription products");
      }

      return response.json();
    },
    staleTime: CUSTOMER_STATE_STALE_TIME,
  });

  const subscriptionsQuery = useQuery({
    queryKey: queryKeys.subscriptions.list(activeOrganizationId),
    queryFn: async (): Promise<SubscriptionsResponse> => {
      const query = new URLSearchParams();
      if (activeOrganizationId) {
        query.set("organizationId", activeOrganizationId);
      }
      const queryString = query.toString();
      const response = await fetch(
        `${env.VITE_API_URL}/api/subscriptions${queryString ? `?${queryString}` : ""}`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch subscriptions");
      }

      return response.json();
    },
    staleTime: SUBSCRIPTIONS_STALE_TIME,
  });

  const membershipStatusQuery = useQuery({
    queryKey: queryKeys.subscriptions.membership(activeOrganizationId),
    queryFn: async (): Promise<MembershipStatusResponse> => {
      const query = new URLSearchParams();
      if (activeOrganizationId) {
        query.set("organizationId", activeOrganizationId);
      }
      const queryString = query.toString();
      const response = await fetch(
        `${env.VITE_API_URL}/api/membership/status${queryString ? `?${queryString}` : ""}`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch membership status");
      }

      return response.json();
    },
    staleTime: SUBSCRIPTIONS_STALE_TIME,
  });

  const checkoutMutation = useMutation({
    mutationFn: async (params: {
      productId: string;
      billingPeriod: "monthly" | "yearly";
      metadata?: Record<string, unknown>;
      organizationId?: string;
    }): Promise<CheckoutResponse> => {
      const selectedProduct = productsQuery.data?.products.find(
        (product) => product.id === params.productId,
      );
      const selectedPlanId = selectedProduct ? parseTierIdFromProduct(selectedProduct) : "free";

      const response = await fetch(`${env.VITE_API_URL}/api/subscriptions/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          productId: params.productId,
          planId: selectedPlanId !== "free" ? selectedPlanId : undefined,
          billingPeriod: params.billingPeriod,
          organizationId: params.organizationId,
          successUrl: `${window.location.origin}/checkout-success`,
          cancelUrl: `${window.location.origin}/pricing?checkout=canceled`,
          metadata: {
            billingPeriod: params.billingPeriod,
            ...params.metadata,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Checkout failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }

      return data;
    },
    onSuccess: (data) => {
      if (isAdminTierOverrideCheckoutResponse(data)) {
        toast.success(getAdminTierOverrideToastMessage(data));
        void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all() });
        return;
      }

      toast.success("Redirecting to checkout...");
    },
    onError: () => {
      toast.error("Failed to start checkout process");
    },
  });

  const planChangeMutation = useMutation({
    mutationFn: async (params: {
      subscriptionId: string;
      productId: string;
      billingPeriod?: "monthly" | "yearly";
      organizationId?: string;
      prorationBehavior: "invoice" | "prorate";
    }): Promise<PlanChangeResult> => {
      const response = await fetch(
        `${env.VITE_API_URL}/api/subscriptions/${params.subscriptionId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            billingPeriod: params.billingPeriod,
            organizationId: params.organizationId ?? activeOrganizationId,
            productId: params.productId,
            prorationBehavior: params.prorationBehavior,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.statusMessage || `Plan change failed: ${response.statusText}`);
      }

      const payload = (await response.json()) as PlanChangeApiResponse;

      if (payload.planChange) {
        if (payload.planChange.direction === "upgrade" && payload.planChange.checkoutUrl) {
          window.location.href = payload.planChange.checkoutUrl;
        }

        return {
          success: true,
          planChange: payload.planChange,
        };
      }

      if (!payload.subscription) {
        throw new Error("Plan change response did not include Subscription or checkout");
      }

      const subscription = payload.subscription;

      return {
        success: true,
        subscription: {
          id: subscription.id,
          status: subscription.status,
          productId: subscription.productId,
          amount: 0,
          currency: "USD",
          currentPeriodEnd: subscription.currentPeriodEnd
            ? new Date(subscription.currentPeriodEnd).toISOString()
            : new Date().toISOString(),
          recurringInterval: "month",
        },
      };
    },
    onSuccess: (data) => {
      if (data.planChange?.direction === "upgrade" && data.planChange.checkoutUrl) {
        toast.success("Redirecting to checkout...");
        return;
      }

      toast.success(
        data.planChange?.membershipDeferredUntil === "renewal"
          ? "Plan change scheduled for renewal."
          : "Plan updated successfully!",
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all() });
    },
    onError: (mutationError: Error) => {
      toast.error(mutationError.message || "Failed to change plan");
    },
  });

  const cancelPendingPlanChangeMutation = useMutation({
    mutationFn: async (subscriptionId: string): Promise<CancelPendingPlanChangeResponse> => {
      const response = await fetch(
        `${env.VITE_API_URL}/api/subscriptions/${subscriptionId}/pending-plan-change`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.statusMessage || errorData.message || "Failed to cancel Pending Plan change",
        );
      }

      return response.json();
    },
    onSuccess: () => {
      toast.success("Pending Plan change canceled.");
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all() });
    },
    onError: (mutationError: Error) => {
      toast.error(mutationError.message || "Failed to cancel Pending Plan change");
    },
  });

  const billingPortalMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (env.VITE_PAYMENT_PROVIDER !== "polar") {
        throw new Error("Billing portal is only available for Polar");
      }

      const portalFn = (
        authClient as {
          customer?: {
            portal?: () => Promise<{
              data?: { url?: string };
              error?: { message?: string };
            }>;
          };
        }
      ).customer?.portal;

      if (!portalFn) {
        throw new Error("Billing portal is not available");
      }

      const result = await portalFn();
      if (result.error) {
        throw new Error(result.error.message || "Failed to open billing portal");
      }
      if (result.data?.url) {
        window.open(result.data.url, "_blank");
      }
    },
    onSuccess: () => {
      toast.success("Opening billing portal...");
    },
    onError: (billingPortalError: Error) => {
      toast.error(billingPortalError.message || "Failed to open billing portal");
    },
  });

  const clearAdminTierOverrideMutation = useMutation({
    mutationFn: async (): Promise<ClearAdminTierOverrideResponse> => {
      const query = new URLSearchParams();
      if (activeOrganizationId) {
        query.set("organizationId", activeOrganizationId);
      }
      const queryString = query.toString();
      const response = await fetch(
        `${env.VITE_API_URL}/api/membership/admin-tier-override${queryString ? `?${queryString}` : ""}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to clear Admin tier override");
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast.success(
        data.changed ? "Admin tier override cleared." : "Admin tier override already cleared.",
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all() });
    },
    onError: () => {
      toast.error("Failed to clear Admin tier override");
    },
  });

  const products = productsQuery.data?.products ?? EMPTY_PRODUCTS;
  const subscriptions = subscriptionsQuery.data?.subscriptions ?? EMPTY_SUBSCRIPTIONS;
  const pendingPlanChangesFromSubscriptions = subscriptions.flatMap((subscription) =>
    subscription.pendingPlanChange ? [subscription.pendingPlanChange] : [],
  );
  const pendingPlanChanges =
    subscriptionsQuery.data?.pendingPlanChanges ??
    (pendingPlanChangesFromSubscriptions.length > 0
      ? pendingPlanChangesFromSubscriptions
      : EMPTY_PENDING_PLAN_CHANGES);

  const activeSubscription = (() => {
    const validSubs = subscriptions.filter(isUsableSubscription);

    // When a downgrade is pending, prefer the original (non-downgrade)
    // subscription so the UI shows the current tier until it expires.
    if (validSubs.length > 1) {
      const nonDowngrade = validSubs.find((sub) => sub.metadata?.proratedDowngrade !== true);
      if (nonDowngrade) {
        return nonDowngrade;
      }
    }

    return validSubs.at(0) ?? null;
  })();

  const pendingPlanChange =
    pendingPlanChanges.find(
      (planChange) =>
        planChange.status === "pending" && planChange.subscriptionId === activeSubscription?.id,
    ) ??
    pendingPlanChanges.find((planChange) => planChange.status === "pending") ??
    null;

  // TODO: Mercado Pago no tiene metadata, no estamos obteniendo ningún tier, esto hay que sacarlo de la base.

  const currentTier: MembershipTier = parseTierIdFromName(
    membershipStatusQuery.data?.data.tier ||
      (activeSubscription?.metadata?.tier as string | undefined) ||
      (activeSubscription?.metadata?.planId as string | undefined) ||
      activeSubscription?.productName,
  );

  const dbPlans = productsQuery.data?.plans;

  // Until the products query answers, assume the provider this build is
  // configured for rather than hardcoding one. Hardcoding "polar" here was a
  // second, invisible default that survived a configured mercadopago build.
  const provider = productsQuery.data?.provider ?? env.VITE_PAYMENT_PROVIDER;
  const tierConfigs = useMemo(
    () => buildTierConfigFromPlansAndProducts(products, dbPlans, provider),
    [products, dbPlans, provider],
  );

  const tierConfig = tierConfigs.find((config) => config.id === currentTier) || null;
  const billingCadence = membershipStatusQuery.data?.data.billingCadence ?? null;
  const hasActiveSubscription =
    membershipStatusQuery.data?.data.hasActiveSubscription === true || Boolean(activeSubscription);

  const rawAdminTierOverride = membershipStatusQuery.data?.data.adminTierOverride;
  const adminTierOverride = useMemo(
    () => parseAdminTierOverride(rawAdminTierOverride),
    [rawAdminTierOverride],
  );
  const isAppAdmin = membershipStatusQuery.data?.data.isAppAdmin === true;

  const isLoading =
    productsQuery.isLoading ||
    subscriptionsQuery.isLoading ||
    membershipStatusQuery.isLoading ||
    checkoutMutation.isPending ||
    planChangeMutation.isPending ||
    cancelPendingPlanChangeMutation.isPending ||
    billingPortalMutation.isPending ||
    clearAdminTierOverrideMutation.isPending;

  const error =
    (productsQuery.error as Error | null) ||
    (subscriptionsQuery.error as Error | null) ||
    (membershipStatusQuery.error as Error | null);

  const checkoutRef = useRef(checkoutMutation.mutateAsync);
  checkoutRef.current = checkoutMutation.mutateAsync;

  const planChangeRef = useRef(planChangeMutation.mutateAsync);
  planChangeRef.current = planChangeMutation.mutateAsync;

  const activeSubIdRef = useRef(activeSubscription?.id);
  activeSubIdRef.current = activeSubscription?.id;

  const resolvePlanChangeProductId = useCallback(
    (tierIdOrProductId: string, billingPeriod: "monthly" | "yearly") => {
      if (products.some((product) => product.id === tierIdOrProductId)) {
        return tierIdOrProductId;
      }

      const tier = tierConfigs.find((entry) => entry.id === tierIdOrProductId);
      return (
        (billingPeriod === "yearly" ? tier?.yearly?.id : tier?.monthly?.id) ?? tierIdOrProductId
      );
    },
    [products, tierConfigs],
  );

  const upgradeToTier = useCallback(
    async (
      tierId: string,
      billingPeriod: "monthly" | "yearly" = "monthly",
      organizationId?: string,
    ) => {
      const checkoutOrganizationId = organizationId ?? activeOrganizationId;
      const productId = resolvePlanChangeProductId(tierId, billingPeriod);

      if (activeSubscription?.id) {
        await planChangeRef.current({
          subscriptionId: activeSubscription.id,
          productId,
          billingPeriod,
          organizationId: checkoutOrganizationId,
          prorationBehavior: "prorate",
        });
        return;
      }

      await checkoutRef.current({
        productId,
        billingPeriod,
        organizationId: checkoutOrganizationId,
        metadata: checkoutOrganizationId ? { organizationId: checkoutOrganizationId } : {},
      });
    },
    [activeSubscription, activeOrganizationId, resolvePlanChangeProductId],
  );

  const changePlan = useCallback(
    (
      productId: string,
      options?: {
        billingPeriod?: "monthly" | "yearly";
        organizationId?: string;
        prorationBehavior?: "invoice" | "prorate";
      },
    ): Promise<PlanChangeResult> => {
      if (!activeSubIdRef.current) {
        throw new Error("No active subscription to change");
      }

      return planChangeRef.current({
        subscriptionId: activeSubIdRef.current,
        productId,
        billingPeriod: options?.billingPeriod,
        organizationId: options?.organizationId ?? activeOrganizationId,
        prorationBehavior: options?.prorationBehavior || "prorate",
      });
    },
    [activeOrganizationId],
  );

  const billingPortalRef = useRef(billingPortalMutation.mutateAsync);
  billingPortalRef.current = billingPortalMutation.mutateAsync;

  const openBillingPortal = useCallback(async () => {
    await billingPortalRef.current();
  }, []);

  const refreshMembership = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all() });
  }, [queryClient]);

  const clearAdminTierOverrideRef = useRef(clearAdminTierOverrideMutation.mutateAsync);
  clearAdminTierOverrideRef.current = clearAdminTierOverrideMutation.mutateAsync;

  const clearAdminTierOverride = useCallback(async () => {
    await clearAdminTierOverrideRef.current();
  }, []);

  const cancelPendingPlanChangeRef = useRef(cancelPendingPlanChangeMutation.mutateAsync);
  cancelPendingPlanChangeRef.current = cancelPendingPlanChangeMutation.mutateAsync;

  const cancelPendingPlanChange = useCallback(
    async (subscriptionId?: string) => {
      const targetSubscriptionId =
        subscriptionId ?? pendingPlanChange?.subscriptionId ?? activeSubIdRef.current;
      if (!targetSubscriptionId) {
        toast.error("No Pending Plan change to cancel");
        return;
      }

      await cancelPendingPlanChangeRef.current(targetSubscriptionId);
    },
    [pendingPlanChange?.subscriptionId],
  );

  const hasFeature = useCallback(
    (feature: string) => {
      if (!tierConfig) {
        return false;
      }
      return tierConfig.features.some((featureValue) =>
        featureValue.toLowerCase().includes(feature.toLowerCase()),
      );
    },
    [tierConfig],
  );

  const hasPermission = useCallback(
    (permission: string) => {
      if (!tierConfig) {
        return false;
      }
      return tierConfig.permissions.includes(permission);
    },
    [tierConfig],
  );

  const isWithinLimit = useCallback(
    (limitKey: string, currentUsage: number) => {
      if (!tierConfig?.limits) {
        return true;
      }
      const limit = tierConfig.limits[limitKey as keyof typeof tierConfig.limits];
      if (limit === undefined || limit === -1) {
        return true;
      }
      return currentUsage <= limit;
    },
    [tierConfig],
  );

  const canUpgrade = currentTier !== "ultimate";

  const getPlanChangeType = useCallback(
    (targetTierId: string, targetBillingPeriod?: "monthly" | "yearly"): PlanChangeType => {
      const tierHierarchy: Record<string, number> = {
        free: 0,
        basic: 1,
        pro: 2,
        ultimate: 3,
      };

      const currentLevel = tierHierarchy[currentTier] ?? 0;
      const targetLevel = tierHierarchy[targetTierId] ?? 0;

      if (targetLevel > currentLevel) {
        return "upgrade";
      }
      if (targetLevel < currentLevel) {
        return "downgrade";
      }
      if (targetBillingPeriod && billingCadence && targetBillingPeriod !== billingCadence) {
        return "period_change";
      }
      return "same";
    },
    [currentTier, billingCadence],
  );

  const value = useMemo<MembershipContextValue>(
    () => ({
      currentTier,
      tierConfig,
      isLoading,
      error,
      subscriptions,
      activeSubscription,
      pendingPlanChanges,
      pendingPlanChange,
      orders: EMPTY_ORDERS,
      meters: EMPTY_METERS,
      benefits: EMPTY_BENEFITS,
      billingCadence,
      hasActiveSubscription,
      upgradeToTier,
      changePlan,
      openBillingPortal,
      refreshMembership,
      clearAdminTierOverride,
      cancelPendingPlanChange,
      hasFeature,
      hasPermission,
      isWithinLimit,
      canUpgrade,
      getPlanChangeType,
      adminTierOverride,
      isAppAdmin,
      isClearingAdminTierOverride: clearAdminTierOverrideMutation.isPending,
      isCancelingPendingPlanChange: cancelPendingPlanChangeMutation.isPending,
    }),
    [
      currentTier,
      tierConfig,
      isLoading,
      error,
      subscriptions,
      activeSubscription,
      pendingPlanChanges,
      pendingPlanChange,
      billingCadence,
      hasActiveSubscription,
      upgradeToTier,
      changePlan,
      openBillingPortal,
      refreshMembership,
      clearAdminTierOverride,
      cancelPendingPlanChange,
      hasFeature,
      hasPermission,
      isWithinLimit,
      canUpgrade,
      getPlanChangeType,
      adminTierOverride,
      isAppAdmin,
      clearAdminTierOverrideMutation.isPending,
      cancelPendingPlanChangeMutation.isPending,
    ],
  );

  return <MembershipContext.Provider value={value}>{children}</MembershipContext.Provider>;
}
